import type { LocalizedPathKey } from '@/utils/seo/localized-paths'

// Einzige Quelle der Hauptnavigation. Desktop-Mega-Menue und mobiles Akkordeon
// lesen beide hier ab, damit die zwei Varianten nicht auseinanderlaufen.
//
// Labels sind Schluessel unter `navigation` in src/messages/{de,en}.json. Der
// Test in __tests__/menu.test.ts prueft, dass jeder Schluessel in beiden
// Sprachen existiert und jeder Pfad in i18n/routing.ts registriert ist.

// Nur statische Pfade: dynamische Templates wie /projekte/[slug] braeuchten
// Params und gehoeren nicht in ein festes Menue.
export type StaticPath = Exclude<LocalizedPathKey, `${string}[${string}`>

export type MenuLink = {
  href: StaticPath
  label: string
  // Uebersichtsseiten nur bei exakt gleichem Pfad als aktiv markieren, sonst
  // leuchtet "KI im Überblick" auch auf jeder KI-Unterseite.
  exact?: boolean
  // Einstiegs-Angebote (Analyse, Beratung) in Akzentfarbe mit Pfeil
  highlight?: boolean
}

export type MenuColumn = {
  title: string
  links: MenuLink[]
}

export type MenuItem =
  | { id: string; label: string; href: StaticPath; match?: string[] }
  // showContact: zusaetzliche Spalte mit Telefon und Mail (nur Desktop, mobil
  // stehen beide ohnehin unter dem Menue)
  | { id: string; label: string; columns: MenuColumn[]; match?: string[]; showContact?: boolean }

export const isMenuWithColumns = (item: MenuItem): item is Extract<MenuItem, { columns: MenuColumn[] }> => 'columns' in item

export const mainMenu: MenuItem[] = [
  {
    id: 'ai',
    label: 'ai',
    columns: [
      {
        title: 'menu.aiSolutions',
        links: [
          { href: '/ki/ki-agenten', label: 'menu.aiAgents' },
          { href: '/ki/ki-chatbot', label: 'menu.aiChatbot' },
          { href: '/lp/ki-automatisierung', label: 'menu.aiAutomation' },
          { href: '/ki/ki-beratung', label: 'menu.aiConsulting' }
        ]
      },
      {
        title: 'menu.aiForShops',
        links: [
          { href: '/ki/ki-automatisierung-woocommerce', label: 'menu.aiWooCommerce' },
          { href: '/ki/ki-automatisierung-shopify', label: 'menu.aiShopify' },
          { href: '/lp/ki-agentur-mittelstand', label: 'menu.aiSme' }
        ]
      },
      {
        title: 'menu.getStarted',
        links: [
          { href: '/ki', label: 'menu.aiOverview', exact: true },
          { href: '/kostenlose-ki-analyse', label: 'menu.freeAiAnalysis', highlight: true }
        ]
      }
    ]
  },
  // Einstieg ueber die Lage des Kunden, die Seite verteilt auf die passenden
  // Landingpages
  { id: 'help', label: 'help', href: '/wo-wir-helfen' },
  {
    id: 'services',
    label: 'services',
    // Uebrige Landingpages (Branchen, Vergleiche) gehoeren ebenfalls hierher
    match: ['/lp'],
    columns: [
      {
        title: 'menu.ecommerce',
        links: [
          { href: '/lp/woocommerce-agentur', label: 'menu.woocommerceAgency' },
          { href: '/lp/shopify-agentur', label: 'menu.shopifyAgency' },
          { href: '/lp/shopware-agentur', label: 'menu.shopwareAgency' },
          { href: '/lp/sap-commerce-cloud', label: 'menu.sapCommerceCloud' },
          { href: '/lp/b2b-e-commerce', label: 'menu.b2bEcommerce' },
          { href: '/lp/online-shop-entwicklung', label: 'menu.onlineStoreDevelopment' }
        ]
      },
      {
        title: 'menu.webAndSoftware',
        links: [
          { href: '/lp/wordpress-agentur', label: 'menu.wordpressAgency' },
          { href: '/lp/app-entwicklung', label: 'menu.appDevelopment' },
          { href: '/lp/individuelle-software', label: 'menu.customSoftware' },
          { href: '/plugins', label: 'menu.plugins' }
        ]
      },
      {
        title: 'menu.care',
        links: [
          { href: '/lp/woocommerce-betreuung', label: 'menu.woocommerceSupport' },
          { href: '/lp/shopify-betreuung', label: 'menu.shopifySupport' },
          { href: '/lp/wordpress-wartung', label: 'menu.wordpressMaintenance' },
          { href: '/lp/website-betreuung', label: 'menu.websiteSupport' },
          { href: '/lp/woocommerce-migration', label: 'menu.woocommerceMigration' }
        ]
      },
      {
        title: 'menu.overview',
        links: [
          { href: '/leistungen', label: 'menu.allServices', exact: true },
          { href: '/leistungen/technologie', label: 'menu.technology' },
          { href: '/kostenlose-beratung', label: 'menu.freeConsultation', highlight: true }
        ]
      }
    ]
  },
  { id: 'projects', label: 'projects', href: '/projekte' },
  {
    id: 'agency',
    label: 'agency',
    showContact: true,
    columns: [
      {
        title: 'menu.company',
        links: [
          { href: '/about', label: 'about' },
          { href: '/stripe-partner', label: 'menu.stripePartner' }
        ]
      },
      {
        title: 'menu.locations',
        links: [
          { href: '/agentur/webagentur-fuerth', label: 'menu.webAgencyFuerth' },
          { href: '/agentur/webagentur-nuernberg', label: 'menu.webAgencyNuremberg' },
          { href: '/agentur/woocommerce-agentur-nuernberg', label: 'menu.woocommerceAgencyNuremberg' },
          { href: '/agentur/ki-agentur-nuernberg', label: 'menu.aiAgencyNuremberg' },
          { href: '/agentur', label: 'menu.allLocations', exact: true }
        ]
      }
    ]
  },
  { id: 'news', label: 'news', href: '/news', match: ['/blog'] },
  { id: 'contact', label: 'contact', href: '/kontakt' }
]

const matchesPath = (pathname: string, prefix: string, exact = false) => pathname === prefix || (!exact && pathname.startsWith(`${prefix}/`))

export const isLinkActive = (pathname: string, link: Pick<MenuLink, 'href' | 'exact'>) => matchesPath(pathname, link.href, link.exact)

// Welcher Hauptpunkt gehoert zur aktuellen Seite? Der laengste passende Pfad
// gewinnt: /lp/ki-automatisierung steht explizit unter KI und schlaegt damit
// das allgemeine /lp der Leistungen. Uebersichtslinks zaehlen hier als Praefix,
// damit /ki/ki-agenten auch ohne eigenen Menuepunkt unter KI landet.
export const getActiveMenuId = (pathname: string, menu: MenuItem[] = mainMenu): string | null => {
  let best: { id: string; length: number } | null = null

  for (const item of menu) {
    const prefixes = [...(isMenuWithColumns(item) ? item.columns.flatMap((column) => column.links.map((link) => link.href)) : [item.href]), ...(item.match ?? [])]

    for (const prefix of prefixes) {
      if (prefix === '/' || !matchesPath(pathname, prefix)) continue
      if (!best || prefix.length > best.length) best = { id: item.id, length: prefix.length }
    }
  }

  return best?.id ?? null
}
